/**
 * @module util/portPicker/resolveNearestPorts
 * Place → candidate PF ports, tiered by geography (metro → country) then distance.
 * Pure + mode-agnostic (works for seaports or airports). PRD §6.2.
 */
import { haversineKm, roundKm } from './geo';
import { NEAREST_DEFAULTS, GeoTier } from './constants';

const norm = (s) => (s == null ? '' : String(s).trim().toLowerCase());

/** Same country by id (preferred) or, failing that, by case-folded country name. */
const sameCountry = (place, port) => {
  if (place.countryId != null && port.CountryId != null) {
    return String(place.countryId) === String(port.CountryId);
  }
  return norm(place.country) !== '' && norm(place.country) === norm(port.Country);
};

const tierFor = (distanceKm, withinMetro, isSameCountry) => {
  if (distanceKm != null && withinMetro) return GeoTier.METRO;
  return isSameCountry ? GeoTier.COUNTRY : GeoTier.OTHER;
};

/**
 * Resolve a picked place to its nearest PF ports.
 * @param {{lat,lng,country?,countryId?}} place
 * @param {Array<{id,Name,Country,CountryId,latitude?,longitude?,lat?,lng?}>} ports
 * @param {{metroKm?:number,maxNearest?:number}} [opts]
 * @returns {Array<{port,distanceKm:(number|null),tier:number}>} nearest first, capped at
 *          maxNearest, always ≥1 when any port exists (closest overall as last resort).
 */
export const resolveNearestPorts = (place, ports, opts = {}) => {
  const { metroKm, maxNearest } = { ...NEAREST_DEFAULTS, ...opts };
  if (!place || !Array.isArray(ports) || ports.length === 0) return [];

  const scored = [];
  const coordlessSameCountry = [];
  for (const port of ports) {
    const distanceKm = haversineKm(place, port);
    if (distanceKm == null) {
      if (sameCountry(place, port)) coordlessSameCountry.push(port); // name/country fallback
      continue;
    }
    const tier = tierFor(distanceKm, distanceKm <= metroKm, sameCountry(place, port));
    scored.push({ port, distanceKm: roundKm(distanceKm), tier });
  }
  scored.sort((a, b) => a.tier - b.tier || a.distanceKm - b.distanceKm);

  // Prefer metro/same-country; only reach for an other-country port to guarantee ≥1.
  const preferred = scored.filter((s) => s.tier <= GeoTier.COUNTRY);
  const base = preferred.length ? preferred : scored.slice(0, 1);
  const picked = base.slice(0, maxNearest);

  // Fill any remaining slots with coordless same-country ports (no distance shown).
  for (const port of coordlessSameCountry) {
    if (picked.length >= maxNearest) break;
    if (!picked.some((p) => p.port.id === port.id)) picked.push({ port, distanceKm: null, tier: GeoTier.COUNTRY });
  }

  // Contract: ≥1 whenever ports exist. If nothing matched (all coordless AND no country
  // match), fall back to the first port(s) so the caller always has a candidate.
  if (!picked.length) {
    return ports.slice(0, maxNearest).map((port) => ({ port, distanceKm: null, tier: GeoTier.OTHER }));
  }
  return picked;
};
